import { useEffect, useState, useCallback } from "react";
import { getWalletMe } from "@/api/wallet";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Cross-component ping so any billable action can ask the credit chip to
 * refresh immediately.
 */
const CREDITS_REFRESH_EVENT = "rubby:credits-refresh";
export function pingCreditsRefresh() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CREDITS_REFRESH_EVENT));
  }
}

export function useCredits() {
  const { user } = useAuth();
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const refetch = useCallback(async () => {
    if (!user) {
      setBalance(null);
      return;
    }
    setLoading(true);
    try {
      const wallet = await getWalletMe();
      setBalance(wallet.balance ?? 0);
    } catch (err) {
      console.warn("[useCredits] fetch error:", err);
      setBalance(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (!user) return;
    const handler = () => {
      void refetch();
    };
    window.addEventListener(CREDITS_REFRESH_EVENT, handler);
    return () => window.removeEventListener(CREDITS_REFRESH_EVENT, handler);
  }, [user, refetch]);

  return { balance, loading, refetch };
}
